<template>
  <div class="busca-usuario" ref="container">
    <div class="campo-input">
      <input
        type="search"
        :value="texto"
        :placeholder="placeholder"
        autocomplete="off"
        @input="aoDigitar($event.target.value)"
        @focus="abrirPainel"
        @keydown.down.prevent="navegar(1)"
        @keydown.up.prevent="navegar(-1)"
        @keydown.enter.prevent="confirmarSelecaoAtiva"
      />
      <button
        v-if="texto"
        type="button"
        class="limpar"
        @click="limparSelecao"
        aria-label="Limpar seleção"
      >
        ×
      </button>
    </div>
    <ul v-if="aberto" class="lista" role="listbox">
      <li v-if="carregando" class="mensagem">Buscando usuários...</li>
      <template v-else>
        <li
          v-for="(usuario, index) in resultados"
          :key="usuario.idUsuario || usuario.usuarioId || usuario.id"
          :class="{ ativo: indiceAtivo === index }"
          role="option"
          @mousedown.prevent="selecionar(usuario)"
        >
          <strong>{{ usuario.nome || usuario.email }}</strong>
          <small>{{ usuario.email || usuario.telefone || 'Sem contato informado' }}</small>
        </li>
        <li v-if="criando" class="mensagem">Cadastrando novo usuário...</li>
        <li
          v-else-if="podeCadastrar"
          class="acao"
          :class="{ ativo: indiceAtivo === resultados.length }"
          role="option"
          @mousedown.prevent="cadastrarUsuario"
        >
          <strong>Adicionar "{{ texto.trim() }}"</strong>
          <small>Salvar este membro para reutilizar no módulo</small>
        </li>
        <li
          v-if="!resultados.length && !podeCadastrar && !criando"
          class="mensagem vazio"
        >
          Nenhum usuário encontrado
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import { buscarUsuariosProjeto, criarUsuarioProjeto } from '../services/api'

export default {
  name: 'BuscaUsuarioGestaoProjetos',
  props: {
    value: {
      type: [Number, String, null],
      default: null
    },
    descricao: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Buscar usuário pelo nome'
    }
  },
  data () {
    return {
      texto: '',
      consulta: '',
      resultados: [],
      carregando: false,
      criando: false,
      aberto: false,
      indiceAtivo: -1,
      usuarioSelecionado: null,
      debounceId: null
    }
  },
  computed: {
    podeCadastrar () {
      const termo = (this.texto || '').trim()
      if (termo.length < 2) return false
      if (this.carregando || this.criando) return false
      const normalizado = termo.toLowerCase()
      return !this.resultados.some(usuario => (usuario?.nome || '').toLowerCase() === normalizado)
    }
  },
  watch: {
    descricao: {
      immediate: true,
      handler (novo) {
        if (!this.usuarioSelecionado && !this.consulta) {
          this.texto = novo || ''
        }
      }
    },
    value (novo) {
      if (!novo) {
        this.usuarioSelecionado = null
        if (!this.consulta) {
          this.texto = ''
        }
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.verificarCliqueExterno)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.verificarCliqueExterno)
    clearTimeout(this.debounceId)
  },
  methods: {
    abrirPainel () {
      this.aberto = true
      if (!this.resultados.length && this.texto.trim().length >= 2) {
        this.buscarUsuarios()
      }
    },
    aoDigitar (valor) {
      this.texto = valor
      this.consulta = valor
      this.aberto = true
      this.indiceAtivo = -1
      clearTimeout(this.debounceId)
      if (!valor) {
        this.resultados = []
        this.$emit('input', null)
        this.$emit('usuario-selecionado', null)
        return
      }
      if (valor.trim().length < 2) {
        this.resultados = []
        return
      }
      this.debounceId = setTimeout(() => {
        this.buscarUsuarios()
      }, 250)
    },
    async buscarUsuarios () {
      const termo = (this.consulta || '').trim()
      if (!termo || termo.length < 2) return
      try {
        this.carregando = true
        const { data } = await buscarUsuariosProjeto({ busca: termo })
        const lista = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : []
        this.resultados = lista.slice(0, 15)
      } catch (error) {
        this.resultados = []
        // eslint-disable-next-line no-console
        console.error('[gestao-projetos] busca-usuario-modulo', error)
        this.$toasted && this.$toasted.error('Não foi possível buscar os usuários cadastrados.')
      } finally {
        this.carregando = false
      }
    },
    async cadastrarUsuario () {
      const nome = (this.texto || '').trim()
      if (!nome || nome.length < 2 || this.criando) return
      try {
        this.criando = true
        const { data } = await criarUsuarioProjeto({ nome })
        if (data) {
          const id = data.idUsuario || data.id
          this.resultados = [
            data,
            ...this.resultados.filter(usuario => (usuario.idUsuario || usuario.id) !== id)
          ]
          this.selecionar(data)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[gestao-projetos] cadastrar-usuario-modulo', error)
        this.$toasted && this.$toasted.error('Não foi possível cadastrar o usuário informado.')
      } finally {
        this.criando = false
      }
    },
    selecionar (usuario) {
      this.usuarioSelecionado = usuario
      this.texto = usuario?.nome || usuario?.email || ''
      this.aberto = false
      this.indiceAtivo = -1
      this.$emit('input', usuario?.idUsuario || usuario?.usuarioId || usuario?.id || null)
      this.$emit('usuario-selecionado', usuario)
    },
    limparSelecao () {
      this.texto = ''
      this.consulta = ''
      this.usuarioSelecionado = null
      this.resultados = []
      this.aberto = false
      this.indiceAtivo = -1
      this.$emit('input', null)
      this.$emit('usuario-selecionado', null)
    },
    verificarCliqueExterno (evento) {
      if (!this.$refs.container) return
      if (!this.$refs.container.contains(evento.target)) {
        this.aberto = false
        this.indiceAtivo = -1
      }
    },
    navegar (direcao) {
      if (!this.aberto) return
      const total = this.resultados.length + (this.podeCadastrar ? 1 : 0)
      if (total === 0) return
      const novoIndice = this.indiceAtivo + direcao
      if (novoIndice < 0) {
        this.indiceAtivo = total - 1
      } else if (novoIndice >= total) {
        this.indiceAtivo = 0
      } else {
        this.indiceAtivo = novoIndice
      }
    },
    confirmarSelecaoAtiva () {
      if (this.indiceAtivo === this.resultados.length && this.podeCadastrar) {
        this.cadastrarUsuario()
        return
      }
      if (this.indiceAtivo >= 0 && this.resultados[this.indiceAtivo]) {
        this.selecionar(this.resultados[this.indiceAtivo])
        return
      }
      if (this.podeCadastrar) {
        this.cadastrarUsuario()
      }
    }
  }
}
</script>

<style scoped>
.busca-usuario {
  position: relative;
}

.campo-input {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe3f0;
  border-radius: 8px;
  padding: 0 8px;
}

.campo-input input {
  flex: 1;
  border: none;
  outline: none;
  padding: 8px 6px;
  font-size: 0.9rem;
  background: transparent;
  color: #0f172a;
}

.campo-input input::placeholder {
  color: #94a3b8;
}

.campo-input .limpar {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1rem;
  cursor: pointer;
  padding: 4px;
}

.lista {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #dbe3f0;
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  z-index: 12;
  padding: 4px 0;
  list-style: none;
  margin: 0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.lista li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.lista li strong {
  font-weight: 600;
  color: #0f172a;
}

.lista li small {
  color: #475569;
  font-size: 0.75rem;
}

.lista li.ativo,
.lista li:hover {
  background: #e8f1ff;
}

.acao {
  border-top: 1px solid #e2e8f0;
  color: #1d4ed8;
}

.acao strong {
  color: #1d4ed8;
}

.acao small {
  color: #475569;
}

.mensagem {
  padding: 10px 12px;
  color: #64748b;
  font-size: 0.8rem;
}

.mensagem.vazio {
  font-style: italic;
  color: #94a3b8;
}
</style>
